import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';

function PriceOverrideTable({ value, setValue, isEditable, label, sourceField, sourceOptions }) {
  const [overrides, setOverrides] = useState({});
  const [localValues, setLocalValues] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (value && typeof value === 'object') {
      setOverrides(value);
      const local = {};
      Object.keys(value).forEach(key => {
        local[key] = value[key].toString();
      });
      setLocalValues(local);
    } else {
      setOverrides({});
      setLocalValues({});
    }
  }, [value]);

  const sourceItems = sourceField ? sourceOptions || [] : [];

  const handlePriceBlur = useCallback(
    (itemId, price) => {
      const newOverrides = { ...overrides };
      const numPrice =
        price === '' || price === null || price === undefined ? null : parseFloat(price);

      if (numPrice === null || isNaN(numPrice)) {
        delete newOverrides[itemId];
      } else {
        newOverrides[itemId] = numPrice;
      }
      setOverrides(newOverrides);
      setValue(newOverrides);
    },
    [overrides, setValue]
  );

  const handlePriceChange = useCallback((itemId, price) => {
    setLocalValues(prev => ({ ...prev, [itemId]: price }));
  }, []);

  if (sourceItems.length === 0) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant='subtitle2' color='text.secondary'>
          {label}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Bitte wählen Sie zuerst {sourceField === 'packages' ? 'Pakete' : 'Upsells'} aus.
        </Typography>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1.5 }}>
          {label}
        </Typography>
        <Stack spacing={1.5}>
          {sourceItems.map(item => {
            const itemId = item.upgradeId || item._id || item.value;
            const itemName = item.name || item.label || '';
            const overridePrice =
              localValues[itemId] !== undefined
                ? localValues[itemId]
                : overrides[itemId] !== undefined
                  ? overrides[itemId].toString()
                  : '';

            return (
              <Box
                key={itemId}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant='body2' fontWeight='medium' sx={{ mb: 1 }}>
                  {itemName}
                </Typography>
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  value={overridePrice}
                  onChange={e => handlePriceChange(itemId, e.target.value)}
                  onBlur={e => handlePriceBlur(itemId, e.target.value)}
                  disabled={!isEditable}
                  placeholder='Preisüberschreibung (optional)'
                  inputProps={{
                    step: '0.01',
                    min: '0',
                  }}
                  label='Preisüberschreibung'
                />
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>
        {label}
      </Typography>
      <TableContainer component={Paper} variant='outlined'>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant='caption' fontWeight='bold'>
                  {sourceField === 'packages' ? 'Paket' : 'Upsell'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='caption' fontWeight='bold'>
                  Preisüberschreibung
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sourceItems.map(item => {
              const itemId = item.upgradeId || item._id || item.value;
              const itemName = item.name || item.label || '';
              const overridePrice =
                localValues[itemId] !== undefined
                  ? localValues[itemId]
                  : overrides[itemId] !== undefined
                    ? overrides[itemId].toString()
                    : '';

              return (
                <TableRow key={itemId}>
                  <TableCell>
                    <Typography variant='body2'>{itemName}</Typography>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type='number'
                      size='small'
                      value={overridePrice}
                      onChange={e => handlePriceChange(itemId, e.target.value)}
                      onBlur={e => handlePriceBlur(itemId, e.target.value)}
                      disabled={!isEditable}
                      placeholder='Standardpreis'
                      inputProps={{
                        step: '0.01',
                        min: '0',
                      }}
                      sx={{ width: '150px' }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default PriceOverrideTable;
